import { useMemo } from 'react';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { SurveyAdmin, SurveyV2 } from '@act/shared/data-transfer-objects';

interface SurveyFormConfigProps {
  allSurveyRecords: SurveyAdmin.SurveyCreatorRecordDto[];
  allLearnMoreRecords: Array<{
    id: number;
    learnMoreContent: { title: string };
  }>;
  currentContent: SurveyV2.CreateSurveyJSONDto & { id?: number };
}

export const useSurveyFormConfig = ({
  allSurveyRecords,
  allLearnMoreRecords,
  currentContent,
}: SurveyFormConfigProps): FormConfig<SurveyV2.CreateSurveyJSONDto> => {
  return useMemo(() => {
    const baseConfig: FormConfig<SurveyV2.CreateSurveyJSONDto> = [
      ...(allSurveyRecords?.length > 0
        ? [
            {
              type: 'input' as const,
              name: 'selectedSurveyCreatorRecordId' as const,
              label: 'Choose a record to edit',
              input: 'select' as const,
              props: {
                fullWidth: true,
                options: [
                  {
                    label: 'Select a record to edit',
                    value: -1,
                  },
                  ...allSurveyRecords.map(record => ({
                    label: record.surveyContent?.name,
                    value: record.id,
                  })),
                ],
              },
            },
          ]
        : []),

      {
        type: 'input' as const,
        name: 'surveyContent.name' as const,
        label: 'Survey Name',
        input: 'text' as const,
        props: {
          required: true,
          placeholder: 'Enter survey name',
        },
      },
      {
        type: 'group' as const,
        items: [
          {
            type: 'input' as const,
            name: 'surveyContent.key' as const,
            label: 'Survey Key',
            input: 'text' as const,
            disabled: true,
            props: {
              placeholder: 'Auto-generated from survey name',
              variant: 'standard',
            },
          },
          {
            type: 'input' as const,
            name: 'surveyContent.isActionSurvey' as const,
            label: 'Is action survey',
            input: 'checkbox' as const,
          },
        ],
      },
      {
        type: 'input' as const,
        name: 'surveyContent.displayName' as const,
        label: 'Survey Display Name',
        input: 'text' as const,
        props: {
          required: true,
          placeholder: 'Enter survey display name',
        },
      },
      {
        type: 'input' as const,
        name: 'surveyContent.summary' as const,
        label: 'Survey Summary',
        input: 'text' as const,
        props: {
          required: true,
          multiline: true,
          minRows: 3,
          maxRows: 3,
          placeholder: 'Enter survey summary',
        },
      },
    ];

    if (allLearnMoreRecords?.length > 0) {
      baseConfig.push({
        type: 'input' as const,
        name: 'selectedLearnMoreRecordId' as const,
        label: 'Choose learn more content for this survey',
        input: 'select' as const,
        props: {
          fullWidth: true,
          options: [
            {
              label: 'Select learn more content',
              value: -1,
            },
            ...allLearnMoreRecords.map(record => ({
              label: record.learnMoreContent.title,
              value: record.id,
            })),
          ],
        },
      });
    }

    return baseConfig;
  }, [allSurveyRecords, allLearnMoreRecords, currentContent]);
};
